import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, Text, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import PortalsInUseList from '@/configuration/components/PortalsInUseList/PortalsInUseList'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'
import { settingsTypes } from '@/configuration/constants'
import { sectionTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import useSettingsType from '@/configuration/pages/ConfigurationPage/hooks/useSettingsType/useSettingsType'

import DeleteEmailTemplateModal from './DeleteEmailTemplateModal/DeleteEmailTemplateModal'
import EmailTemplatesConfigurationSkeleton from './EmailTemplatesConfigurationSkeleton/EmailTemplatesConfigurationSkeleton'
import useEmailTemplatesConfiguration from './hooks/useEmailTemplatesConfiguration/useEmailTemplatesConfiguration'
import SortableTemplateList from './SortableTemplateList/SortableTemplateList'
import * as Styles from './styles'

const EmailTemplatesConfiguration = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
    savingStoreSettingsType,
    hasChanges,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.email_templates,
    isUnifiedDashboard,
    setToastInfo,
  })
  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })
  const {
    loading,
    emailTemplates,
    onToggleTemplate,
    onEdit,
    onCreate,
    onRequestDelete,
    onConfirmDelete,
    onCancelDelete,
    onDuplicate,
    showDeleteModal,
    totalTemplates,
    enabledPortalsWithGlobal,
    hasLocalChanges,
    onSaveDisplay,
    savingDisplay,
    revertChanges,
    dragging,
    onSortEnd,
    onSortStart,
    onSearch,
  } = useEmailTemplatesConfiguration({
    storeId: info.selectedStore.id,
    setToastInfo,
    navigate,
    location,
    configurationMode,
    merchantId: info.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    isUnifiedDashboard,
  })

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: emailTemplates,
    initialElementsToShow: 25,
  })

  const { hasPermission } = useUserPermissions()

  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global

  if (loading || loadingStoreSettingsType) {
    return <EmailTemplatesConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {(hasChanges || hasLocalChanges) && (
        <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.viewCommunications)} />
      )}
      {isUnifiedDashboard && (
        <Styles.PortalsInUseContainer>
          <PortalsInUseList portals={info.returnPortals} portalsInUse={enabledPortalsWithGlobal} />
        </Styles.PortalsInUseContainer>
      )}
      {!isUnifiedDashboard && info.allows_unified_dashboard && (
        <SettingsSection type={settingsType} onChange={setSettingsType} />
      )}
      <Text type="titleM">{t('Correos para clientes')}</Text>
      <Text type="bodyMRegular">
        {t('Configura los correos que recibirán los clientes en función de sus condiciones de envío.')}
      </Text>

      {totalTemplates > 0 && <QueryFilter placeholder={t('Nombre de plantilla, descripción')} onChange={onSearch} />}
      <SortableTemplateList
        templates={elementsToShow}
        onToggleTemplate={onToggleTemplate}
        onEdit={onEdit}
        onDelete={onRequestDelete}
        onDuplicate={onDuplicate}
        disabled={isDisabledByGlobalSettings}
        dragging={dragging}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
      />
      {totalTemplates > 0 && <Divider />}
      <LoadMore
        numberOfElementsToShow={numberOfElementsToShow}
        totalElements={totalElements}
        hasMoreElements={hasMoreElements}
        showMore={showMore}
      />
      {!isDisabledByGlobalSettings && (
        <div>
          <Button variant="text" size="s" onClick={onCreate}>
            <PlusIcon size={24} />
            {t('Añadir nueva plantilla')}
          </Button>
        </div>
      )}
      <SaveChangesButtons
        hasChanges={settingsTypeHasChanges || hasLocalChanges}
        onSave={hasLocalChanges ? onSaveDisplay : onSaveSettingsType}
        onRevertChanges={hasLocalChanges ? revertChanges : revertSettingsType}
        loading={savingStoreSettingsType || savingDisplay}
        disabled={savingStoreSettingsType || savingDisplay}
      />
      <DeleteEmailTemplateModal show={showDeleteModal} onClose={onCancelDelete} onDelete={onConfirmDelete} />
    </Styles.Container>
  )
}

export default EmailTemplatesConfiguration
